import { observable, toJS } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import type { Element, OrderItemResponseArgs } from './order_item2';
import OrderItem, { convertOrderItemResponseToArgs } from './order_item2';
import TravelerInformation, { type TravelerInformationArgs } from './traveler_information';
import TransportElement, { convertTransportElementResponseToArgs } from './transport_element2';
import HotelElement, { convertHotelElementResponseToArgs } from './hotel_element2';
import CancelChargeElement, { convertCancelChargeElementResponseToArgs } from './cancel_charge_element2';
import ChangeFeeElement, { convertChangeFeeElementResponseToArgs } from './change_fee_element2';
import SupportFeeElement, {
  convertSupportFeeElementResponseToArgs
} from './support_fee_element/support_fee_element2';
import ShippingElement, { convertShippingElementResponseToArgs } from './shipping_element/shipping_element2';
import WifiElement, { convertWifiElementResponseToArgs } from './wifi_element/wifi_element2';
import RoomElement, { convertRoomElementResponseToArgs } from './room_element/room_element2';
import KitElement, { convertKitElementResponseToArgs } from './kit_element/kit_element2';
import TravelAuthorizationElement, {
  convertTravelAuthorizationElementResponseToArgs
} from './travel_authorization_element2';
import InsuranceElement, { convertInsuranceElementResponseToArgs } from './insurance_element2';
import MileageNumber from './mileage_number2';
import type { ArrangerResponseArgs } from './arranger/arranger/arranger2';
import Arranger, { convertArrangerResponseToArgs } from './arranger/arranger/arranger2';
import Trip, { convertTripResponseToArgs } from './trip/trip2';
import OrderItemPrice from './order_item_price/order_item_price2';
import OrderItemOriginalPrice from './order_item_original_price2';
import OrderItemPayment from './order_item_payment/order_item_payment2';
import Margin from './margin2';
import MarginType from './organization/margin_type2';
import OrderItemPriceDetail from './order_item_price_detail2';
import type { TripResponseArgs } from './trip/trip2';

const TRAVELER_INFORMATION_TEMP_ID_RANGE = 1000000000;

// ホテル予約情報の型定義
export type HotelReservation = {
  reserveUrl: string;
  singleReserveUrl: string;
  alternativePlanUrl?: string;
  rakutenTravelRConnectBookingSucceed: boolean;
};

export type TravelerConfirmationStatus = 'unrequested' | 'requested' | 'confirmed';

export type OrderResponseArgs = {
  id: number;
  order_items?: OrderItemResponseArgs[];
  created_at?: string;
  confirmed_at?: string | null;
  gmo_id?: string;
  payment_transaction_id?: string;
  checked_at?: string | null;
  checker?: ArrangerResponseArgs | null;
  journal_created_at?: string | null;
  journal_creator?: ArrangerResponseArgs | null;
  foreign_air_fee?: number;
  domestic_air_fee?: number;
  shinkansen_fee?: number;
  shipping_category_options?: { [key: string]: string };
  wifi_provider_options?: { [key: string]: string };
  hotel_element_provider_options?: { [key: string]: string };
  car_type_options?: [string, string][];
  car_provider_options?: [string, string][];
  traveler_confirmation_status?: TravelerConfirmationStatus;
  trip?: TripResponseArgs;
};

export type OrderArgs = {
  id: number;
  showFee: boolean;
  orderItems: OrderItem[];
  createdAt?: Moment;
  confirmedAt?: Moment | null;
  gmoId?: string;
  paymentTransactionId?: string;
  checkedAt?: Moment | null;
  checker?: Arranger | null;
  journalCreatedAt?: Moment | null;
  journalCreator?: Arranger | null;
  foreignAirFee?: number;
  domesticAirFee?: number;
  shinkansenFee?: number;
  shippingCategoryOptions?: { [key: string]: string };
  wifiProviderOptions?: { [key: string]: string };
  hotelElementProviderOptions?: { [key: string]: string };
  carTypeOptions?: [string, string][];
  carProviderOptions?: [string, string][];
  trip?: Trip;
  travelerConfirmationStatus?: TravelerConfirmationStatus;
};

export const convertOrderResponseToArgs = (response: OrderResponseArgs, showFee: boolean, trip?: Trip) => {
  const args: OrderArgs = {
    id: response.id,
    showFee,
    orderItems:
      response.order_items?.map(
        orderItem =>
          new OrderItem(
            convertOrderItemResponseToArgs(
              orderItem,
              showFee,
              response.wifi_provider_options,
              response.shipping_category_options,
              response.hotel_element_provider_options
            )
          )
      ) || [],
    createdAt: response.created_at ? moment(response.created_at) : undefined,
    confirmedAt: response.confirmed_at ? moment(response.confirmed_at) : null,
    gmoId: response.gmo_id,
    paymentTransactionId: response.payment_transaction_id,
    checkedAt: response.checked_at ? moment(response.checked_at) : null,
    checker: response.checker ? new Arranger(convertArrangerResponseToArgs(response.checker)) : null,
    journalCreatedAt: response.journal_created_at ? moment(response.journal_created_at) : null,
    journalCreator: response.journal_creator
      ? new Arranger(convertArrangerResponseToArgs(response.journal_creator))
      : null,
    foreignAirFee: response.foreign_air_fee,
    domesticAirFee: response.domestic_air_fee,
    shinkansenFee: response.shinkansen_fee,
    shippingCategoryOptions: response.shipping_category_options,
    wifiProviderOptions: response.wifi_provider_options,
    hotelElementProviderOptions: response.hotel_element_provider_options,
    carTypeOptions: response.car_type_options,
    carProviderOptions: response.car_provider_options,
    trip: trip || (response.trip ? new Trip(convertTripResponseToArgs(response.trip, showFee)) : undefined),
    travelerConfirmationStatus: response.traveler_confirmation_status
  };
  return args;
};

export default class Order {
  @observable id: number;
  @observable showFee: boolean;
  @observable orderItems: OrderItem[];
  @observable deletedItems: OrderItem[] = [];
  @observable createdAt?: Moment;
  @observable confirmedAt?: Moment | null;
  @observable gmoId?: string;
  @observable paymentTransactionId?: string;
  @observable checkedAt?: Moment | null;
  @observable checker?: Arranger | null;
  @observable journalCreatedAt?: Moment | null;
  @observable journalCreator?: Arranger | null;
  @observable foreignAirFee?: number;
  @observable domesticAirFee?: number;
  @observable shinkansenFee?: number;
  @observable shippingCategoryOptions?: { [key: string]: string };
  @observable wifiProviderOptions?: { [key: string]: string };
  @observable hotelElementProviderOptions?: { [key: string]: string };
  @observable carTypeOptions?: [string, string][];
  @observable carProviderOptions?: [string, string][];
  @observable trip?: Trip;
  @observable travelerConfirmationStatus?: TravelerConfirmationStatus;

  constructor(args: OrderArgs) {
    this.id = args.id;
    this.showFee = args.showFee;
    this.orderItems = this.sortOrderItemsByStartTime(args.orderItems);
    this.createdAt = args.createdAt;
    this.confirmedAt = args.confirmedAt;
    this.gmoId = args.gmoId;
    this.paymentTransactionId = args.paymentTransactionId;
    this.checkedAt = args.checkedAt;
    this.checker = args.checker;
    this.journalCreatedAt = args.journalCreatedAt;
    this.journalCreator = args.journalCreator;
    this.foreignAirFee = args.foreignAirFee;
    this.domesticAirFee = args.domesticAirFee;
    this.shinkansenFee = args.shinkansenFee;
    this.shippingCategoryOptions = args.shippingCategoryOptions;
    this.wifiProviderOptions = args.wifiProviderOptions;
    this.hotelElementProviderOptions = args.hotelElementProviderOptions;
    this.carTypeOptions = args.carTypeOptions;
    this.carProviderOptions = args.carProviderOptions;
    this.trip = args.trip;
    this.travelerConfirmationStatus = args.travelerConfirmationStatus;
  }

  get totalPrice() {
    return this.orderItems.reduce((acc, item) => acc + (item.price?.totalPrice ?? 0), 0);
  }

  removeOldFees() {
    this.foreignAirFee = 0;
    this.domesticAirFee = 0;
    this.shinkansenFee = 0;
  }

  orderItemsUpdatedAt() {
    const targetItems = [...this.orderItems.filter(item => item.isEditing), ...this.deletedItems];
    const timestampData = targetItems.map(item => {
      if (item.traceId && item.updatedAt) {
        return {
          trace_id: item.traceId,
          updated_at: item.updatedAt.format()
        };
      }
      return null;
    });

    return timestampData.filter(data => data !== null);
  }

  handleRemoveItem(item: OrderItem) {
    this.deletedItems.push(item);
    const index = this.orderItems.indexOf(item);
    if (index > -1) {
      this.orderItems.splice(index, 1);
    }
  }

  handleAddItem() {
    // 新しいアイテムを追加
    const newItem = new OrderItem({
      id: 0,
      showFee: this.showFee,
      orderItemCategory: '',
      orderItemCategoryText: '',
      orderItemMappings: [],
      travelerInformations: [],
      transports: [],
      hotels: [],
      cancelCharges: [],
      changeFees: [],
      supportFees: [],
      shippings: [],
      wifis: [],
      rooms: [],
      kits: [],
      travelAuthorizations: [],
      insurances: [],
      soldOut: false,
      isEditing: true,
      wifiProviderOptions: this.wifiProviderOptions,
      shippingCategoryOptions: this.shippingCategoryOptions,
      hotelElementProviderOptions: this.hotelElementProviderOptions,
      waiting: false
    });
    this.orderItems.push(newItem);
  }

  get description() {
    // 注文の説明を返す
    return this.orderItems
      .map(item => item.description)
      .filter(text => text)
      .join('\n');
  }

  get packageBookingUrl() {
    // パッケージ予約URLを取得
    const item = this.orderItems[0];
    if (item) {
      return item.packageBookingUrl;
    }
    return undefined;
  }

  get hotelReservations(): HotelReservation[] {
    // ホテル予約情報を取得
    const urls: HotelReservation[] = [];
    this.orderItems.forEach(item => {
      item.elements.forEach((element: Element) => {
        // HotelElementの場合のみ処理
        if (this.isHotelElement(element) && element.reserveUrl) {
          urls.push({
            reserveUrl: element.reserveUrl,
            singleReserveUrl: this.toSingleReserveUrl(element.reserveUrl),
            alternativePlanUrl: this.toAlternativePlanUrl(element.reserveUrl) || undefined,
            rakutenTravelRConnectBookingSucceed:
              element.rakutenTravelRConnectBookingInfos?.length === element.roomNum || false
          });
        }
      });
    });
    return urls;
  }

  private isHotelElement(element: Element): element is HotelElement {
    return 'type' in element && element.type === 'hotel';
  }

  private toSingleReserveUrl(original: string) {
    if (original) {
      return original.replace(/f_otona_su=\d/i, 'f_otona_su=1');
    }
    return original;
  }

  private toAlternativePlanUrl(original: string) {
    if (!original) return undefined;

    try {
      const url = new URL(original);

      if (!url.searchParams.get('f_hi1')) return undefined;
      if (!url.searchParams.get('f_hi2')) return undefined;

      const fNo = url.searchParams.get('f_no');
      const fOtonaSu = url.searchParams.get('f_otona_su');
      const fS1 = url.searchParams.get('f_s1');
      const fS2 = url.searchParams.get('f_s2');
      const fY1 = url.searchParams.get('f_y1');
      const fY2 = url.searchParams.get('f_y2');
      const fY3 = url.searchParams.get('f_y3');
      const fY4 = url.searchParams.get('f_y4');
      const fHeyaSu = url.searchParams.get('f_heya_su');
      const fNen1 = url.searchParams.get('f_hi1')?.split('-')[0];
      const fTuki1 = parseInt(url.searchParams.get('f_hi1')?.split('-')[1] || '0', 10);
      const fHi1 = parseInt(url.searchParams.get('f_hi1')?.split('-')[2] || '0', 10);
      const fNen2 = url.searchParams.get('f_hi2')?.split('-')[0];
      const fTuki2 = parseInt(url.searchParams.get('f_hi2')?.split('-')[1] || '0', 10);
      const fHi2 = parseInt(url.searchParams.get('f_hi2')?.split('-')[2] || '0', 10);

      return (
        `https://hotel.travel.rakuten.co.jp/hotelinfo/plan/` +
        `?f_no=${fNo}&f_teikei=&f_flg=PLAN&f_otona_su=${fOtonaSu}&f_s1=${fS1}` +
        `&f_s2=${fS2}&f_y1=${fY1}&f_y2=${fY2}&f_y3=${fY3}&f_y4=${fY4}&f_kin=` +
        `&f_kin2=&f_heya_su=${fHeyaSu}&f_nen1=${fNen1}&f_tuki1=${fTuki1}&f_hi1=${fHi1}` +
        `&f_nen2=${fNen2}&f_tuki2=${fTuki2}&f_hi2=${fHi2}&f_hak=&f_tel=&f_target_flg=` +
        `&f_tscm_flg=&f_p_no=&f_custom_code=&f_search_type=&f_service=&f_campaign=&f_camp_id=&f_static=1&f_tel=`
      );
    } catch (e) {
      return undefined;
    }
  }

  isNeedReCalcMarginAmount(): boolean {
    return this.orderItems.some(item => item.isNeedReCalcMarginAmount());
  }

  get validationErrors(): string[][][] {
    const targetItems = this.orderItems.filter(item => item.isEditing);

    return targetItems.map(item => {
      return item.validationErrors();
    });
  }

  get traceItemDescriptions(): { [key: string]: string } {
    const result: { [key: string]: string } = {};

    this.orderItems
      .filter(item => item.traceId)
      .forEach(item => {
        if (item.traceId) {
          result[item.traceId] = item.mappingDescription;
        }
      });

    return result;
  }

  get updateParams() {
    const targetItems = this.orderItems.filter(item => item.isEditing);
    return {
      order_items: targetItems.map(item => item.updateParams),
      deleted_trace_ids: this.deletedItems.map(item => item.traceId),
      order_items_updated_at: this.orderItemsUpdatedAt()
    };
  }

  get isTicketingExpired() {
    return this.orderItems.some(item => item.isTicketingExpired);
  }

  get endTime() {
    const times = this.orderItems.map(item => item.endTime).filter(t => !!t);

    if (times.length === 0) return null;

    return times.sort((a, b) => a.toDate().getTime() - b.toDate().getTime())[times.length - 1];
  }

  get totalPriceWithBuffer() {
    return this.orderItems.reduce((acc, item) => acc + (item.price?.budgetFrameAmount ?? 0), 0);
  }

  get totalBufferAmount() {
    return this.orderItems.reduce((acc, item) => acc + (item.bufferAmount ?? 0), 0);
  }

  // OrderItemの表示順制御
  // ソート順序1：ElementのStartTimeの最小値
  // ソート順序2：trace_id
  private sortOrderItemsByStartTime(items: OrderItem[]): OrderItem[] {
    return [...items].sort((itemA, itemB) => {
      const startA = this.orderItemStartTimestamp(itemA);
      const startB = this.orderItemStartTimestamp(itemB);

      if (startA && startB) {
        if (startA !== startB) {
          return startA - startB;
        }
      } else if (startA) {
        return -1;
      } else if (startB) {
        return 1;
      }

      const traceA = itemA.traceId ?? '';
      const traceB = itemB.traceId ?? '';
      return `${traceA}`.localeCompare(`${traceB}`);
    });
  }

  // ソート処理の速度・可読性のため、UNIXタイムで返す
  private orderItemStartTimestamp(item: OrderItem): number | null {
    const startMoment = item.getStartTimestamp();
    return startMoment ? startMoment.valueOf() : null;
  }

  handleCopyItem(item: OrderItem): void {
    // mobxのプロキシオブジェクトをcloneしようとするとエラーが出るため、toJSでプレーンなオブジェクトに変換してからcloneする
    const plainItem = toJS(item);
    const clonedData = cloneDeep(plainItem);
    clonedData.id = 0;
    clonedData.traceId = undefined;
    clonedData.isEditing = true;
    clonedData.billedAt = undefined;
    clonedData.jobType = 'reserve';
    clonedData.status = 9; // NEED_INSTRUCTION = 9 # 発券の指示

    // paymentがtoJS(item)によりプレーンオブジェクトになるため、初期化し直す
    // OrderItemPaymentのコンストラクタでpayment_typeの初期値が設定される
    clonedData.payment = clonedData.payment ? new OrderItemPayment(clonedData.payment) : new OrderItemPayment({});

    if (item.price) {
      // OrderItemPriceのコピーについて
      // プリミティブ型は代入時に値コピーされるが、オブジェクトや配列は参照型を渡すため、deep copyが必要
      // 代入だとコピー元と先が連動したり、validationErrors() や updateParams()が発火しない
      //   margin?: Margin;
      //   priceDetails: OrderItemPriceDetail[];
      //   originalPrices: OrderItemOriginalPrice[];
      //   marginType?: MarginType;
      const clonedMargin = item.price.margin ? new Margin({ amount: item.price.margin.amount }) : undefined;
      const clonedPriceDetails = item.price.priceDetails?.map(d => {
        const cloneDetail = cloneDeep(toJS(d));
        cloneDetail.detailTraceId = undefined;
        return new OrderItemPriceDetail(cloneDetail);
      });
      const clonedOriginalPrices = item.price.originalPrices?.map(op => {
        const cloneOp = cloneDeep(toJS(op));
        cloneOp.originalPriceTraceId = undefined;
        return new OrderItemOriginalPrice(cloneOp);
      });
      const clonedMarginType = item.price.marginType
        ? new MarginType(cloneDeep(toJS(item.price.marginType)))
        : undefined;

      // 株主優待券、回数券の紐づけはコピーしない
      //   bulkTicketIds: string[];
      //   bulkTicketFragmentIds: number[];
      //   shareholderTicketIds: number[];
      const clonedBulkTicketIds: string[] = [];
      const clonedBulkTicketFragmentIds: number[] = [];
      const clonedShareholderTicketIds: number[] = [];

      clonedData.price = new OrderItemPrice({
        id: 0,
        price: item.price.price,
        showFee: item.price.showFee,
        margin: clonedMargin,
        marginType: clonedMarginType,
        peopleNum: item.price.peopleNum,
        sectionNum: item.price.sectionNum,
        bulkTicketIds: clonedBulkTicketIds,
        bulkTicketFragmentIds: clonedBulkTicketFragmentIds,
        shareholderTicketIds: clonedShareholderTicketIds,
        originalPrices: clonedOriginalPrices,
        priceDetails: clonedPriceDetails,
        refund: item.price.refund,
        selfLoss: item.price.selfLoss,
        debtWaiver: item.price.debtWaiver,
        lossWorkflowUrl: item.price.lossWorkflowUrl
      });
    }

    // 新しいOrderItemインスタンスを作成
    const clone = new OrderItem(clonedData);

    const materializeElement = (element: Element) => {
      const params = (
        'updateParams' in element && element.updateParams ? element.updateParams : toJS(element)
      ) as any;

      switch (element.type) {
        case 'transport':
          return new TransportElement(convertTransportElementResponseToArgs(params));
        case 'hotel':
          return new HotelElement(convertHotelElementResponseToArgs(params, this.hotelElementProviderOptions));
        case 'cancel':
          return new CancelChargeElement(convertCancelChargeElementResponseToArgs(params));
        case 'change_fee':
          return new ChangeFeeElement(convertChangeFeeElementResponseToArgs(params));
        case 'support_fee':
          return new SupportFeeElement(convertSupportFeeElementResponseToArgs(params));
        case 'shipping':
          return new ShippingElement(convertShippingElementResponseToArgs(params, this.shippingCategoryOptions));
        case 'wifi':
          return new WifiElement(convertWifiElementResponseToArgs(params, this.wifiProviderOptions));
        case 'room':
          return new RoomElement(convertRoomElementResponseToArgs(params));
        case 'kit':
          return new KitElement(convertKitElementResponseToArgs(params));
        case 'travel_authorization':
          return new TravelAuthorizationElement(convertTravelAuthorizationElementResponseToArgs(params));
        case 'insurance':
          return new InsuranceElement(convertInsuranceElementResponseToArgs(params));
        default:
          return cloneDeep(toJS(element)) as Element;
      }
    };

    clone.elements = item.elements.map(materializeElement);
    clone.transports = clone.elements.filter((el): el is TransportElement => el instanceof TransportElement);
    clone.hotels = clone.elements.filter((el): el is HotelElement => el instanceof HotelElement);
    clone.cancelCharges = clone.elements.filter(
      (el): el is CancelChargeElement => el instanceof CancelChargeElement
    );
    clone.changeFees = clone.elements.filter((el): el is ChangeFeeElement => el instanceof ChangeFeeElement);
    clone.supportFees = clone.elements.filter((el): el is SupportFeeElement => el instanceof SupportFeeElement);
    clone.shippings = clone.elements.filter((el): el is ShippingElement => el instanceof ShippingElement);
    clone.wifis = clone.elements.filter((el): el is WifiElement => el instanceof WifiElement);
    clone.rooms = clone.elements.filter((el): el is RoomElement => el instanceof RoomElement);
    clone.kits = clone.elements.filter((el): el is KitElement => el instanceof KitElement);
    clone.travelAuthorizations = clone.elements.filter(
      (el): el is TravelAuthorizationElement => el instanceof TravelAuthorizationElement
    );
    clone.insurances = clone.elements.filter((el): el is InsuranceElement => el instanceof InsuranceElement);

    const generateTempTravelerId = () =>
      Math.floor(Math.random() * (9999 + 1 - 1000)) + TRAVELER_INFORMATION_TEMP_ID_RANGE;

    const clonedTravelerInformations = item.travelerInformations.map(traveler => {
      const travelerArgs: TravelerInformationArgs = {
        id: generateTempTravelerId(),
        name: traveler.name,
        type: traveler.type,
        userId: traveler.userId,
        birthday: traveler.birthday ? traveler.birthday.clone() : undefined,
        userName: traveler.userName,
        lastNameKana: traveler.lastNameKana,
        firstNameKana: traveler.firstNameKana,
        lastNameRoman: traveler.lastNameRoman,
        firstNameRoman: traveler.firstNameRoman,
        middleNameRoman: traveler.middleNameRoman,
        flightLastName: traveler.flightLastName,
        flightMiddleName: traveler.flightMiddleName,
        flightFirstName: traveler.flightFirstName,
        flightFirstNameRoman: traveler.flightFirstNameRoman,
        flightLastNameRoman: traveler.flightLastNameRoman,
        flightMiddleNameRoman: traveler.flightMiddleNameRoman,
        flightFirstNameKana: traveler.flightFirstNameKana,
        flightLastNameKana: traveler.flightLastNameKana,
        flightBirthday: traveler.flightBirthday ? traveler.flightBirthday.clone() : undefined,
        flightGender: traveler.flightGender,
        flightTel: traveler.flightTel,
        airSeat: traveler.airSeat,
        mileageNumber: traveler.mileageNumber,
        mileageNumbers: traveler.mileageNumbers?.map(
          mileageNumber => new MileageNumber({ carrier: mileageNumber.carrier, number: mileageNumber.number })
        ),
        passportNumber: traveler.passportNumber,
        passportExpire: traveler.passportExpire,
        passportIssuedCountry: traveler.passportIssuedCountry,
        shinkansenSeat: traveler.shinkansenSeat,
        shinkansenSeatInfo: traveler.shinkansenSeatInfo,
        shinkansenSeatType: traveler.shinkansenSeatType,
        shinkansenSeatTypeText: traveler.shinkansenSeatTypeText,
        shinkansenTicketType: traveler.shinkansenTicketType,
        shinkansenTicketTypeText: traveler.shinkansenTicketTypeText,
        showRailwaySeat: traveler.showRailwaySeat,
        travelerInformationShinkansenTypes: traveler.travelerInformationShinkansenTypes?.map(type => ({
          idx: type.idx,
          shinkansenType: type.shinkansenType,
          shinkansenSeatType: type.shinkansenSeatType,
          shinkansenSeat: type.shinkansenSeat,
          shinkansenTicketType: type.shinkansenTicketType
        })),
        exicNumber: traveler.exicNumber,
        exicPassword: traveler.exicPassword,
        exicCardId: traveler.exicCardId,
        user: traveler.user
      };

      return new TravelerInformation(travelerArgs);
    });

    clone.travelerInformations = clonedTravelerInformations;
    clone.handlePeopleNumChange(clone.travelerInformations.length);

    const index = this.orderItems.indexOf(item);
    this.orderItems.splice(index + 1, 0, clone);
  }
}
